import { StructureDocumentType } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { structureDocumentsQueryKeys } from "../query-keys/structure-documents.query-keys";
import structureQueryKeys from "../query-keys/structure.query-keys";
import { FileSchemaType } from "../schemas";

export type CreateStructureDocumentBody = {
  name: string;
  file?: FileSchemaType;
  link?: string;
  thumbnail: FileSchemaType;
};

const createStructureDocument = async (
  structureId: string,
  body: CreateStructureDocumentBody,
) => {
  try {
    const { data } = await privateAxios.post<StructureDocumentType>(
      `/structures/${structureId}/documents`,
      body,
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message || error.message;
    }
    throw error;
  }
};

export const useCreateStructureDocumentMutation = (structureId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStructureDocumentBody) =>
      createStructureDocument(structureId ?? "mine", body),
    onSuccess: () => {
      if (structureId) {
        queryClient.invalidateQueries({
          queryKey: structureDocumentsQueryKeys.list(structureId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: structureDocumentsQueryKeys.list("mine"),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
    },
  });
};

const deleteStructureDocument = async (
  structureId: string,
  documentId: string,
) => {
  try {
    await privateAxios.delete(
      `/structures/${structureId}/documents/${documentId}`,
    );
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message || error.message;
    }
    throw error;
  }
};

export const useDeleteStructureDocumentMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      deleteStructureDocument(structureId ?? "mine", documentId),
    onSuccess: (_, documentId) => {
      queryClient.invalidateQueries({
        queryKey: structureDocumentsQueryKeys.detail(documentId),
      });
      queryClient.invalidateQueries({
        queryKey: structureDocumentsQueryKeys.list(structureId ?? "mine"),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
    },
  });
};
